import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import './TransactionMonitoring.css';
import Loader from '../../components/shared/Loader';
import { useAuthStore } from '../../store/authStore';
import { usePermissions } from '../../hooks/usePermissions';
import {
  BsArrowRepeat,
  BsArrowCounterclockwise,
  BsCreditCard2Back,
} from 'react-icons/bs';
import { useAsyncEffect } from '../../hooks/useAsyncEffect';
import { DataGrid, type ColDef, type GridApi } from '../../components/ui/table';
import type { IDatasource } from 'ag-grid-community';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Alert, AlertDescription } from '../../components/ui/alert';
import { Textarea } from '../../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import { GRID_LIST_FETCH_SIZE, ActiveGridFiltersBar } from '../../components/shared/agGridCells';
import {
  GridColumnFilterPanel,
  formatDateRangeFilterLabel,
} from '../../components/shared/GridColumnFilterPanel';
import { type ActiveGridFilter } from '../../utils/gridColumnFilters';
import {
  buildTransactionColumnFilterFields,
  createTransactionColumnDefs,
  TRANSACTION_COLUMN_LABELS,
} from './table/columnDefs';
import {
  transactionGridOptions,
  GRID_PAGE_SIZE,
  GRID_PAGE_SIZE_SELECTOR,
} from './table/gridOptions';
import TransactionFilters from './components/TransactionFilters';
import TransactionModeSelect from './components/TransactionModeSelect';
import TransactionToolbar from './components/TransactionToolbar';
import ReferralMerchantSelect from './components/ReferralMerchantSelect';
import {
  fetchAllOrdersForStats,
  fetchOrderTransactions,
  fetchOrdersList,
  resendWebhook,
  markTransactionRefund,
  markTransactionChargeback,
  fetchTransactionDccDetailsBatch,
  fetchTransactionFilterLookups,
  fetchOwnReferralMerchants,
} from './api/api';
import { type MappedOrder, buildOrdersPayload, mapAgGridFilterModelToOrderFilters, openTransactionStatusPage, resolveRowTransactionId } from './utils/utils';
import { downloadExcelExport, EXPORT_TYPES } from '../../utils/backendExport';
import { SEARCH_DEBOUNCE_MS, EMPTY_ANALYTICS, EMPTY_TRANSACTION_COLUMN_FILTERS } from './constants/constants';
import { type OrderAnalytics, type OrderFilter, type OrderModeFilter, type TransactionColumnFilterKey, type TransactionColumnFilters, type TransactionDccDetail, type TransactionFilterLookups, type TransactionGridContext, type TransactionOrderRow } from './models/types';
import { commonTextFilterParams } from '../../lib/agGridOptions';
import { showToast } from '../../store/toastStore';
import { useNavigate } from 'react-router-dom';
import TransactionDetailDrawer from './components/TransactionDetailDrawer';

// Module-level so its reference never changes: an inline object literal here fed DataGrid's own
// mergedDefaultColDef useMemo a "new" defaultColDef on every render, which independently
// invalidated that memo and fed the same runaway column-recreation cascade described below.
// filterOptions "equals" matches the Filters drawer / backend exact-match dynamicFilters
// (OrderSpecifications.dynamicFilters): "contains" would send a partial string the API treats
// as an exact value and quietly return no rows.
const TRANSACTION_GRID_DEFAULT_COL_DEF = {
  suppressAutoSize: true,
  filterParams: {
    ...commonTextFilterParams,
    filterOptions: ['contains'],
  },
};

const TransactionMonitoring: React.FC = () => {
  const navigate = useNavigate();
  const isMerchantUser = useAuthStore((state) => state.user?.userType) === "MERCHANT";
  const { canExport } = usePermissions();
  // Export is a gated functionality on this page (Transaction Monitoring = TRAN-MON-09).
  const canExportTransactions = canExport('TRAN-MON-09');
  const authUser = useAuthStore((state) => state.user);
  const isSuperAdminUser =
    String(authUser?.adminType ?? authUser?.role ?? '').trim().toUpperCase() === 'SUPER_ADMIN';
  // Every plain ADMIN (never SUPER_ADMIN) is a referral admin: not an opt-in flag, just what
  // AdminType.ADMIN means. They get a plain merchant dropdown instead of the full Filters drawer
  // (see ReferralMerchantSelect): they're restricted server-side to just these merchants anyway,
  // so a one-click switch between "all of mine combined" and one at a time is more direct than
  // opening a checkbox-list panel built for dozens of columns.
  const isReferralUser =
    !isMerchantUser && String(authUser?.adminType ?? authUser?.role ?? '').trim().toUpperCase() === 'ADMIN';
  const [referralMerchantOptions, setReferralMerchantOptions] = useState<
    { id: number; name: string }[]
  >([]);
  useAsyncEffect(async (isActive) => {
    if (!isReferralUser) return;
    try {
      const merchants = await fetchOwnReferralMerchants();
      if (isActive()) setReferralMerchantOptions(merchants);
    } catch (err) {
      console.error('Failed to load assigned merchants:', err);
    }
  }, [isReferralUser]);
  const [statsOrders, setStatsOrders] = useState<MappedOrder[]>([]);
  const [filterLookups, setFilterLookups] = useState<TransactionFilterLookups | null>(null);
  // Status-tab counts (All/Completed/Failed/...): sourced from the `analytics` object every
  // getOrders response already returns for free (backend-computed count queries against the
  // current filter/mode, not a row dump), instead of the dedicated ~1000-row statsOrders fetch
  // below. Updated by the grid datasource on each page fetch so it reflects the current filter/mode.
  const [analytics, setAnalytics] = useState<OrderAnalytics>(EMPTY_ANALYTICS);
  // Gates only the initial page-shell spinner (before the grid UI renders at all): must resolve
  // on its own, independent of any data fetch. tableLoading covers "data is being fetched" once
  // the page shell is up.
  const [initialLoading, setInitialLoading] = useState(true);
  useEffect(() => {
    setInitialLoading(false);
  }, []);
  const [tableLoading, setTableLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const gridApiRef = useRef<GridApi<TransactionOrderRow> | null>(null);

  // Table view's own datasource (see tableDatasource below) sets this directly to whichever
  // page it just fetched from the server: DCC detail is fetched for exactly that slice, not the
  // full dataset.
  const [visiblePageRows, setVisiblePageRows] = useState<MappedOrder[]>([]);

  const [showModal, setShowModal] = useState(false);
  const [selected, setSelected] = useState<MappedOrder | null>(null);
  const [statusFilter, setStatusFilter] = useState<OrderFilter>("all");
  const [modeFilter, setModeFilter] = useState<OrderModeFilter>("all");
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [exporting, setExporting] = useState(false);
  const [columnFilters, setColumnFilters] = useState<TransactionColumnFilters>(
    EMPTY_TRANSACTION_COLUMN_FILTERS,
  );
  const [filterPanelOpen, setFilterPanelOpen] = useState(false);
  // Bumped when AG Grid's header filters change so ActiveGridFiltersBar can re-read
  // getFilterModel() without putting those values into React state (avoids a second
  // datasource reset: the Infinite Row Model already refetches on filter change).
  const [gridFilterEpoch, setGridFilterEpoch] = useState(0);
  // When true, the next columnFilters-driven datasource reset is skipped because the
  // change originated from clearing/syncing the AG Grid filter model itself.
  const skipDatasourceResetRef = useRef(false);
  const filter = statusFilter;
  // Table datasource reads latest filter state via this ref (stable datasource identity).
  const tableFiltersRef = useRef({ filter, search: debouncedSearch, columnFilters, modeFilter });

  const [modalTxns, setModalTxns] = useState<any[]>([]);
  const [loadingModal, setLoadingModal] = useState(false);
  // Admin-only DCC detail for modal related transactions.
  const [dccDetails, setDccDetails] = useState<Record<string, TransactionDccDetail>>({});
  // Admin-only DCC detail, keyed by transaction id: powers both the "Related Transactions"
  // section in the View modal and the Base/Converted Amount/Currency grid columns.
  const [dccByTransactionId, setDccByTransactionId] = useState<
    Record<string, TransactionDccDetail>
  >({});
  const [resendWebhookTarget, setResendWebhookTarget] = useState<MappedOrder | null>(null);
  const [resendWebhookLoading, setResendWebhookLoading] = useState(false);
  const [resendWebhookError, setResendWebhookError] = useState<string | null>(null);
  // Super-admin-only "Mark as Refund"/"Mark as Chargeback": one shared dialog for both, since
  // they're identical except for label and which endpoint gets called.
  const [markActionTarget, setMarkActionTarget] = useState<
    { row: MappedOrder; kind: 'REFUND' | 'CHARGEBACK' } | null
  >(null);
  const [markActionReason, setMarkActionReason] = useState('');
  const [markActionLoading, setMarkActionLoading] = useState(false);
  const [markActionError, setMarkActionError] = useState<string | null>(null);
  // Read inside the bulk-fetch effect below without adding dccByTransactionId as a dependency:
  // otherwise every merge into that state would immediately re-trigger the effect.
  const dccByTransactionIdRef = useRef(dccByTransactionId);
  useEffect(() => {
    dccByTransactionIdRef.current = dccByTransactionId;
  }, [dccByTransactionId]);
  // Every transaction id this effect has ever attempted, success or failure: checked instead of
  // dccByTransactionId itself, and populated synchronously before the fetch even starts. Without
  // this, an id whose fetch failed (or came back null) never lands in dccByTransactionId, so the
  // very next time this effect re-runs it looks "un-fetched" again and gets retried: forever,
  // for that same id.
  const attemptedDccIdsRef = useRef<Set<string>>(new Set());

  // Previously the grid's DCC columns only ever showed data after a row's order had already been
  // opened in the View modal (handleView populated dccByTransactionId as a side effect): every
  // other row silently showed "—" even when DCC genuinely applied, since nothing fetched DCC detail
  // for the grid itself. This proactively fills it in for whatever page of rows is currently
  // ON SCREEN (visiblePageRows): admin-only (merchants never see these columns at all). Changing
  // page recomputes
  // visiblePageRows, which naturally fetches DCC detail for the new page's rows only.
  useEffect(() => {
    if (isMerchantUser || visiblePageRows.length === 0) return;

    const transactionIds = [...new Set(
      visiblePageRows
        .map((row) => resolveRowTransactionId(row))
        .filter((id): id is string => id != null && id.length > 0 && !attemptedDccIdsRef.current.has(id)),
    )];
    if (transactionIds.length === 0) return;

    transactionIds.forEach((id) => attemptedDccIdsRef.current.add(id));

    let cancelled = false;
    (async () => {
      try {
        const byId = await fetchTransactionDccDetailsBatch(transactionIds);
        if (cancelled) return;
        setDccByTransactionId((prev) => ({ ...prev, ...byId }));
      } catch (err) {
        console.error('Failed to fetch DCC details for grid:', err);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [visiblePageRows, isMerchantUser]);

  useEffect(() => {
    const timeout = window.setTimeout(() => {
      setDebouncedSearch(search);
    }, SEARCH_DEBOUNCE_MS);

    return () => window.clearTimeout(timeout);
  }, [search]);

  const handleRemoveColumnFilter = useCallback((key: string) => {
    // Update the ref synchronously so an immediate AG Grid getRows (from onFilterChanged)
    // already sees the cleared drawer value: setState alone would lag one tick behind.
    tableFiltersRef.current = {
      ...tableFiltersRef.current,
      columnFilters: {
        ...tableFiltersRef.current.columnFilters,
        [key]: null,
      },
    };
    setColumnFilters((prev) => ({
      ...prev,
      [key]: null,
    }));
    const api = gridApiRef.current;
    if (api?.getColumnFilterModel(key)) {
      skipDatasourceResetRef.current = true;
      void api.setColumnFilterModel(key, null).then(() => {
        api.onFilterChanged();
      });
    }
  }, []);

  const handleClearColumnFilters = useCallback(() => {
    tableFiltersRef.current = {
      ...tableFiltersRef.current,
      columnFilters: EMPTY_TRANSACTION_COLUMN_FILTERS,
    };
    setColumnFilters(EMPTY_TRANSACTION_COLUMN_FILTERS);
    const api = gridApiRef.current;
    if (api && Object.keys(api.getFilterModel() ?? {}).length > 0) {
      skipDatasourceResetRef.current = true;
      api.setFilterModel(null);
      api.onFilterChanged();
    }
  }, []);

  const handleApplyColumnFilters = useCallback(
    (values: Record<string, string[] | null>) => {
      // Drawer is the source of truth after Apply: clear any lingering header-funnel filters
      // so they can't override the drawer selection in getRows' merge (native wins).
      // Deliberately skip onFilterChanged here; the columnFilters effect resets the datasource.
      gridApiRef.current?.setFilterModel(null);
      setColumnFilters({
        ...EMPTY_TRANSACTION_COLUMN_FILTERS,
        ...(Object.fromEntries(
          (Object.keys(EMPTY_TRANSACTION_COLUMN_FILTERS) as TransactionColumnFilterKey[]).map(
            (key) => [key, values[key]?.length ? values[key] : null],
          ),
        ) as TransactionColumnFilters),
      });
    },
    [],
  );

  const fetchStatsOrders = useCallback(async (fullCount?: number) => {
    try {
      // Respects the current Sandbox/Live/All selection so the status tab counts (All/
      // Completed/Failed/...) reflect only what's actually selected, not always the grand total.
      const { rows, totalElements } = await fetchAllOrdersForStats(fullCount, modeFilter);
      setStatsOrders(rows);
      if (
        rows.length < totalElements &&
        rows.length >= GRID_LIST_FETCH_SIZE &&
        !fullCount
      ) {
        await fetchStatsOrders(totalElements);
      }
    } catch (err) {
      console.error('Failed to fetch order stats:', err);
    }
  }, [modeFilter]);

  // statsOrders still powers listing-derived options (Txn Id, Amount, Currency, …).
  // Merchant / Acquirer / Acquirer Terminal / Terminal come from their own list APIs
  // (getMerchants, getAcquirers, getAllAcquirerTerminals, getAllMerchantTerminals) so the
  // drawer isn't limited to whatever order rows happened to be loaded.
  useAsyncEffect(async (isActive) => {
    if (!filterPanelOpen) return;
    await fetchStatsOrders();
    if (!isActive()) return;
  }, [fetchStatsOrders, filterPanelOpen]);

  useAsyncEffect(async (isActive) => {
    if (!filterPanelOpen || isMerchantUser || filterLookups) return;
    try {
      const lookups = await fetchTransactionFilterLookups();
      if (isActive()) setFilterLookups(lookups);
    } catch (err) {
      console.error('Failed to load filter lookups:', err);
    }
  }, [filterPanelOpen, isMerchantUser, filterLookups]);

  // AG Grid's Infinite Row Model fetches in blocks of `cacheBlockSize`, which is a COMPLETELY
  // separate option from `paginationPageSize`: confirmed directly against the installed AG Grid
  // source: cacheBlockSize defaults to a hardcoded 100 when not explicitly set, with no link to
  // paginationPageSize at all. Left unset, every fetch requests 100 rows regardless of what the
  // page-size selector shows (10/20/50/100): this is exactly what caused the DCC batch call to
  // always cover ~100 transaction ids even when only 10 were actually displayed. cacheBlockSize IS
  // reactive (not "initial only": AG Grid resets its cache automatically when this prop changes),
  // so keeping it in state driven by the selector's own onPaginationChanged event is the correct,
  // supported fix: both values always come from the same state, so they can't drift apart.
  const [tablePageSize, setTablePageSize] = useState(GRID_PAGE_SIZE);
  const handleTablePaginationChanged = useCallback(() => {
    const api = gridApiRef.current;
    if (!api) return;
    const newSize = api.paginationGetPageSize();
    if (newSize && newSize !== tablePageSize) {
      setTablePageSize(newSize);
    }
  }, [tablePageSize]);

  useEffect(() => {
    tableFiltersRef.current = { filter, search: debouncedSearch, columnFilters, modeFilter };
  }, [filter, debouncedSearch, columnFilters, modeFilter]);

  // Table view's actual data source: reads the latest filter state via tableFiltersRef rather than
  // rebuilding the datasource object on every filter change, since AG Grid's Infinite Row Model
  // expects a stable `datasource` reference; re-assigning it (see the effect below) is what
  // actually tells the grid "throw away everything cached and start over from row 0".
  const tableDatasource = useMemo<IDatasource>(
    () => ({
      getRows: async (params) => {
        const pageSize = params.endRow - params.startRow;
        const page = Math.floor(params.startRow / pageSize);
        setTableLoading(true);
        setError(null);
        try {
          // Fold AG Grid's native header filters into the same columnFilters → buildOrdersPayload
          // path the Filters drawer uses (request.filters / fromDate/toDate / mode). Prefer
          // params.filterModel; fall back to api.getFilterModel() for enableFilterHandlers edge cases.
          const rawFilterModel =
            (params.filterModel as Record<string, unknown> | null | undefined) ??
            (params.api?.getFilterModel() as Record<string, unknown> | null | undefined) ??
            null;
          const {
            columnFilters: nativeColumnFilters,
            modeFilter: nativeModeOverride,
          } = mapAgGridFilterModelToOrderFilters(rawFilterModel);

          // Native header filters win over the drawer for the same column (most recent interaction).
          const mergedColumnFilters: TransactionColumnFilters = {
            ...tableFiltersRef.current.columnFilters,
            ...nativeColumnFilters,
          };

          const { rows, totalElements, analytics: pageAnalytics } = await fetchOrdersList(
            buildOrdersPayload({
              filter: tableFiltersRef.current.filter,
              search: tableFiltersRef.current.search,
              columnFilters: mergedColumnFilters,
              modeFilter: nativeModeOverride ?? tableFiltersRef.current.modeFilter,
              page,
              size: pageSize,
            }),
          );
          setVisiblePageRows(rows);
          setAnalytics(pageAnalytics);
          params.successCallback(rows, totalElements);
        } catch (err: any) {
          console.error("Failed to fetch transactions page:", err);
          setError(err.response?.data?.message || "Failed to load transactions");
          params.failCallback();
        } finally {
          setTableLoading(false);
        }
      },
    }),
    [],
  );

  // Filter/search/mode changed: reset the infinite cache back to row 0 and refetch with the new
  // filters. Re-assigning the same datasource reference is the documented way to force a full
  // reset (not just refreshInfiniteCache, which keeps whatever page the user was already on).
  useEffect(() => {
    if (skipDatasourceResetRef.current) {
      skipDatasourceResetRef.current = false;
      return;
    }
    gridApiRef.current?.setGridOption("datasource", tableDatasource);
  }, [statusFilter, debouncedSearch, columnFilters, modeFilter, tableDatasource]);

  const handleGridFilterChanged = useCallback(() => {
    // Refresh ActiveGridFiltersBar; Infinite Row Model already re-calls getRows with the new
    // filterModel, so we deliberately do NOT write into columnFilters here (that would double-fetch).
    setGridFilterEpoch((n) => n + 1);
  }, []);

  const handleRefresh = useCallback(() => {
    // Re-fetches the blocks already on screen and keeps the current page, same as a
    // single-row status change. A full datasource reset would jump back to row 0.
    gridApiRef.current?.refreshInfiniteCache();
  }, []);

  const handleExport = async () => {
    setExporting(true);
    try {
      const {
        columnFilters: nativeColumnFilters,
        modeFilter: nativeModeOverride,
      } = mapAgGridFilterModelToOrderFilters(
        gridApiRef.current?.getFilterModel() as Record<string, unknown> | null | undefined,
      );
      const mergedColumnFilters: TransactionColumnFilters = {
        ...columnFilters,
        ...nativeColumnFilters,
      };

      await downloadExcelExport({
        type: EXPORT_TYPES.ORDERS,
        fileNamePrefix: `orders-${filter}`,
        errorMessage: 'Failed to export orders.',
        payload: buildOrdersPayload({
          filter,
          search: debouncedSearch,
          columnFilters: mergedColumnFilters,
          modeFilter: nativeModeOverride ?? modeFilter,
        }),
      });
    } finally {
      setExporting(false);
    }
  };

  const openResendWebhookDialog = useCallback((row: MappedOrder) => {
    setResendWebhookTarget(row);
    setResendWebhookError(null);
  }, []);

  const closeResendWebhookDialog = useCallback(() => {
    if (resendWebhookLoading) return;
    setResendWebhookTarget(null);
    setResendWebhookError(null);
  }, [resendWebhookLoading]);

  const confirmResendWebhook = async () => {
    const merchantId = resendWebhookTarget?.merchantId?.trim();
    const transactionId = resendWebhookTarget
      ? resolveRowTransactionId(resendWebhookTarget)
      : null;
    if (!merchantId || !transactionId) return;

    setResendWebhookLoading(true);
    setResendWebhookError(null);
    try {
      await resendWebhook(merchantId, transactionId);
      showToast('Webhook resent successfully.', 'success');
      setResendWebhookTarget(null);
    } catch (err: any) {
      console.error('Failed to resend webhook:', err);
      setResendWebhookError(
        err.response?.data?.message || err.message || 'Failed to resend webhook.',
      );
    } finally {
      setResendWebhookLoading(false);
    }
  };

  const openMarkActionDialog = useCallback((row: MappedOrder, kind: 'REFUND' | 'CHARGEBACK') => {
    setMarkActionTarget({ row, kind });
    setMarkActionReason('');
    setMarkActionError(null);
  }, []);

  const closeMarkActionDialog = useCallback(() => {
    if (markActionLoading) return;
    setMarkActionTarget(null);
    setMarkActionError(null);
  }, [markActionLoading]);

  const confirmMarkAction = async () => {
    const transactionId = markActionTarget
      ? resolveRowTransactionId(markActionTarget.row)
      : null;
    if (!markActionTarget || !transactionId) return;

    setMarkActionLoading(true);
    setMarkActionError(null);
    try {
      const reason = markActionReason.trim() || undefined;
      if (markActionTarget.kind === 'REFUND') {
        await markTransactionRefund(transactionId, reason);
        showToast('Transaction marked as refund.', 'success');
      } else {
        await markTransactionChargeback(transactionId, reason);
        showToast('Transaction marked as chargeback.', 'success');
      }
      setMarkActionTarget(null);
      // Re-pulls the current page only, unlike the full setGridOption("datasource", ...) reset
      // used for filter/search changes elsewhere: a single-row status change doesn't need to
      // reset the user's scroll position.
      gridApiRef.current?.refreshInfiniteCache();
    } catch (err: any) {
      console.error('Failed to update transaction status:', err);
      setMarkActionError(
        err.response?.data?.message || err.message || 'Failed to update transaction status.',
      );
    } finally {
      setMarkActionLoading(false);
    }
  };

  const handleView = async (t: MappedOrder) => {
    setSelected(t);
    setShowModal(true);
    setDccDetails({});
    if (isMerchantUser) {
      setModalTxns([]);
      setLoadingModal(false);
      return;
    }

    setLoadingModal(true);
    try {
      const content = await fetchOrderTransactions(t.id);
      setModalTxns(content);

      const transactionIds: string[] = content
        .map((tx: any) => tx.transactionId)
        .filter((id: unknown): id is string => typeof id === 'string' && id.length > 0);

      const byId = await fetchTransactionDccDetailsBatch(transactionIds);
      setDccDetails(byId);
      setDccByTransactionId((prev) => ({ ...prev, ...byId }));
    } catch (err) {
      console.error(err);
      setModalTxns([]);
    } finally {
      setLoadingModal(false);
    }
  };

  // Drawer state + AG Grid header filters, merged the same way getRows / export do.
  // Used by ActiveGridFiltersBar and the Filters drawer so both surfaces show the same applied set.
  const mergedColumnFilters = useMemo<TransactionColumnFilters>(() => {
    void gridFilterEpoch;
    const {
      columnFilters: nativeColumnFilters,
      modeFilter: nativeMode,
    } = mapAgGridFilterModelToOrderFilters(
      gridApiRef.current?.getFilterModel() as Record<string, unknown> | null | undefined,
    );
    return {
      ...columnFilters,
      ...nativeColumnFilters,
      // Mode is mapped to request.mode (not filters.mode) for the API, but the drawer still
      // has a Mode field: surface Sandbox/Live there so an AG Grid mode filter is visible.
      ...(nativeMode
        ? { mode: [nativeMode === "sandbox" ? "Sandbox" : "Live"] }
        : {}),
    };
  }, [columnFilters, gridFilterEpoch]);

  const activeColumnFilters = useMemo<ActiveGridFilter[]>(() => {
    return (Object.entries(mergedColumnFilters) as [TransactionColumnFilterKey, string[] | null][])
      .filter(([, value]) => Boolean(value?.length))
      .map(([key, value]) => ({
        key,
        label: TRANSACTION_COLUMN_LABELS[key],
        value:
          key === 'dateOnly'
            ? formatDateRangeFilterLabel(value)
            : (value as string[]).join(', '),
      }));
  }, [mergedColumnFilters]);

  const columnFilterFields = useMemo(() => {
    const fields = buildTransactionColumnFilterFields(
      statsOrders,
      isMerchantUser,
      filterLookups,
    );
    // Ensure AG Grid–typed values appear in the drawer's multi-select options so they
    // render as selected (SearchableMultiSelect only highlights values present in options).
    return fields.map((field) => {
      if (field.type === "dateRange") return field;
      const applied = mergedColumnFilters[field.key as TransactionColumnFilterKey];
      if (!applied?.length) return field;
      const missing = applied.filter((value) => !field.options.includes(value));
      if (!missing.length) return field;
      return {
        ...field,
        options: [...field.options, ...missing].sort((a, b) => a.localeCompare(b)),
      };
    });
  }, [statsOrders, isMerchantUser, mergedColumnFilters, filterLookups]);

  const transactionColumnDefs = useMemo<ColDef<TransactionOrderRow>[]>(
    () => createTransactionColumnDefs(isMerchantUser, isReferralUser, isSuperAdminUser),
    [isMerchantUser, isReferralUser, isSuperAdminUser],
  );

  const transactionGridContext = useMemo<TransactionGridContext>(
    () => ({
      isMerchantUser,
      isSuperAdminUser,
      resolveRowTransactionId: (row) =>
        resolveRowTransactionId(row as MappedOrder),
      dccByTransactionId,
      onView: (row) => {
        void handleView(row as MappedOrder);
      },
      onOpenStatus: openTransactionStatusPage,
      onResendWebhook: (row) => {
        openResendWebhookDialog(row as MappedOrder);
      },
      onMarkRefund: (row) => {
        openMarkActionDialog(row as MappedOrder, 'REFUND');
      },
      onMarkChargeback: (row) => {
        openMarkActionDialog(row as MappedOrder, 'CHARGEBACK');
      },
    }),
    [isMerchantUser, isSuperAdminUser, dccByTransactionId, openResendWebhookDialog, openMarkActionDialog],
  );

  useEffect(() => {
    gridApiRef.current?.refreshCells({ force: true });
  }, [dccByTransactionId]);

  // Stable reference across renders: AG Grid's React wrapper re-applies the whole option set
  // (recreating column defs, refreshing the model) whenever this object gets a new identity. With
  // an inline object literal here, that happened on every render, which previously fed back into
  // a recompute -> setState -> re-render -> new object loop, tripping "Maximum update depth
  // exceeded" and starving React Router's own pending navigation update behind this page's
  // runaway re-render loop: kept memoized for the same reason even though it no longer wires up
  // the recompute that originally caused it.
  const handleGridReady = useCallback((event: { api: GridApi<TransactionOrderRow> }) => {
    gridApiRef.current = event.api;
  }, []);

  const transactionGridOptionsMemo = useMemo(
    () => ({
      ...transactionGridOptions,
      onPaginationChanged: handleTablePaginationChanged,
      onFilterChanged: handleGridFilterChanged,
    }),
    [handleTablePaginationChanged, handleGridFilterChanged],
  );

  const renderTransactionTable = () => (
    <div className="dh-table-section tm-table-sections">
      <Card className="tm-table-card tm-table-toolbar-card">
        <div className="tm-table-panel">
          <div className="tm-table-toolbar-row">
            <TransactionFilters
              statusFilter={statusFilter}
              onStatusFilterChange={setStatusFilter}
              loading={tableLoading}
              analytics={analytics}
            />
            <div className="tm-table-toolbar-actions">
              <TransactionToolbar
                search={search}
                onSearchChange={setSearch}
                activeColumnFilterCount={activeColumnFilters.length}
                onOpenFilters={() => setFilterPanelOpen(true)}
                onExport={handleExport}
                exporting={exporting}
                loading={tableLoading}
                hasRows={visiblePageRows.length > 0}
                showExport={canExportTransactions}
                hideFilterButton={isReferralUser}
                merchantSelect={
                  isReferralUser && (
                    <ReferralMerchantSelect
                      merchants={referralMerchantOptions}
                      value={mergedColumnFilters.merchantName?.[0] ?? null}
                      onChange={(merchantName) =>
                        handleApplyColumnFilters({
                          merchantName: merchantName ? [merchantName] : null,
                        })
                      }
                      disabled={tableLoading}
                    />
                  )
                }
              />
            </div>
          </div>
        </div>
      </Card>
      <Card className="tm-table-card tm-table-grid-card overflow-hidden">
        <ActiveGridFiltersBar
          filters={activeColumnFilters}
          onRemove={handleRemoveColumnFilter}
          onClearAll={handleClearColumnFilters}
        />
        <DataGrid<TransactionOrderRow>
          rowModelType="infinite"
          datasource={tableDatasource}
          cacheBlockSize={tablePageSize}
          columnDefs={transactionColumnDefs}
          context={transactionGridContext}
          defaultColDef={TRANSACTION_GRID_DEFAULT_COL_DEF}
          includeAuditColumns={false}
          loading={tableLoading}
          pagination
          paginationPageSize={tablePageSize}
          paginationPageSizeSelector={GRID_PAGE_SIZE_SELECTOR}
          height="100%"
          className="tm-ag-grid"
          onGridReady={handleGridReady}
          gridOptions={transactionGridOptionsMemo}
        />
      </Card>
    </div>
  );

  return (
    <div className="tm-page dh-page-fill">
      <header className="tm-console-header">
        {/* <div className="tm-console-kicker">
          <span className="tm-console-kicker-label">
            <span className="tm-console-kicker-dot" aria-hidden />
            Live intelligence
          </span>
          <span className="tm-console-kicker-sep" aria-hidden />
          <time className="tm-console-kicker-date" dateTime={new Date().toISOString().slice(0, 10)}>
            {formatHeroDate()}
          </time>
        </div> */}
        <div className="tm-console-header-row">
          <div className="tm-console-header-copy">
            <h1 className="tm-console-title">Transaction Monitoring</h1>
            {/* <p className="tm-console-sub">
              Monitor payments, investigate declines, and act on risk as it happens.
            </p> */}
          </div>
          <div className="tm-console-header-actions">
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="tm-console-action-btn tm-console-action-btn--outline gap-1.5"
              onClick={handleRefresh}
              disabled={tableLoading}
            >
              <BsArrowRepeat className="h-3 w-3 shrink-0" aria-hidden />
              Refresh
            </Button>
            <TransactionModeSelect
              value={modeFilter}
              onChange={setModeFilter}
              disabled={tableLoading}
            />
            <Button
              type="button"
              size="sm"
              className="tm-console-action-btn"
              onClick={() => navigate('/dashboard/transaction-dashboard')}
            >
              View Dashboard
            </Button>
          </div>
        </div>
      </header>

      {error && <div className="tm-error-banner">{error}</div>}

      {initialLoading ? (
        <div className="tm-console-loading">
          <Loader size="lg" label="Loading transactions..." />
        </div>
      ) : (
        <>
          {renderTransactionTable()}

          <TransactionDetailDrawer
            open={showModal}
            onOpenChange={setShowModal}
            order={selected}
            isMerchantUser={isMerchantUser}
            loadingRelated={loadingModal}
            relatedTransactions={modalTxns}
            dccDetails={dccDetails}
          />
        </>
      )}

      <GridColumnFilterPanel
        open={filterPanelOpen}
        onOpenChange={setFilterPanelOpen}
        fields={columnFilterFields}
        values={mergedColumnFilters}
        multiple
        onApply={handleApplyColumnFilters}
        onClear={handleClearColumnFilters}
      />

      <Dialog
        open={!!resendWebhookTarget}
        onOpenChange={(isOpen) => !isOpen && closeResendWebhookDialog()}
      >
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <BsArrowRepeat className="h-5 w-5 text-primary" aria-hidden />
              Resend Webhook
            </DialogTitle>
            <DialogDescription>
              Resend the webhook notification for this transaction. Review the details below before
              confirming.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-2 rounded-lg border border-border bg-muted/20 p-4">
          {!isMerchantUser && (
            <div className="flex justify-between gap-4 text-sm">
              <span className="text-muted-foreground">Merchant</span>
              <span className="text-right font-medium">
                {resendWebhookTarget?.merchantName || '—'}
              </span>
            </div>
            )}
            {!isMerchantUser && (
              <div className="flex justify-between gap-4 text-sm">
                <span className="text-muted-foreground">Merchant ID</span>
                <span className="text-right font-medium">
                  {resendWebhookTarget?.merchantId || '—'}
                </span>
              </div>
            )}
            <div className="flex justify-between gap-4 text-sm">
              <span className="text-muted-foreground">Transaction ID</span>
              <span className="text-right font-medium">
                {resendWebhookTarget?.transactionId || '—'}
              </span>
            </div>
          </div>

          {resendWebhookError && (
            <Alert variant="destructive">
              <AlertDescription>{resendWebhookError}</AlertDescription>
            </Alert>
          )}

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={closeResendWebhookDialog}
              disabled={resendWebhookLoading}
            >
              Cancel
            </Button>
            <Button
              type="button"
              size="sm"
              onClick={confirmResendWebhook}
              disabled={
                resendWebhookLoading ||
                !resendWebhookTarget?.merchantId ||
                !resolveRowTransactionId(resendWebhookTarget)
              }
            >
              {resendWebhookLoading ? 'Sending…' : 'Resend Webhook'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
        open={!!markActionTarget}
        onOpenChange={(isOpen) => !isOpen && closeMarkActionDialog()}
      >
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {markActionTarget?.kind === 'CHARGEBACK' ? (
                <BsCreditCard2Back className="h-5 w-5 text-primary" aria-hidden />
              ) : (
                <BsArrowCounterclockwise className="h-5 w-5 text-primary" aria-hidden />
              )}
              Mark as {markActionTarget?.kind === 'CHARGEBACK' ? 'Chargeback' : 'Refund'}
            </DialogTitle>
            <DialogDescription>
              This inserts a new transaction record marking this transaction as{' '}
              {markActionTarget?.kind === 'CHARGEBACK' ? 'chargeback' : 'refunded'} no acquirer
              API call is made. The original transaction record is left untouched.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-2 rounded-lg border border-border bg-muted/20 p-4">
            <div className="flex justify-between gap-4 text-sm">
              <span className="text-muted-foreground">Transaction ID</span>
              <span className="text-right font-medium">
                {markActionTarget?.row.transactionId || '—'}
              </span>
            </div>
            <div className="flex justify-between gap-4 text-sm">
              <span className="text-muted-foreground">Amount</span>
              <span className="text-right font-medium">
                {markActionTarget
                  ? `${markActionTarget.row.amount} ${markActionTarget.row.currency}`
                  : '—'}
              </span>
            </div>
          </div>

          <div className="space-y-1.5">
            <label className="text-sm text-muted-foreground" htmlFor="markActionReason">
              Reason (optional)
            </label>
            <Textarea
              id="markActionReason"
              value={markActionReason}
              onChange={(e) => setMarkActionReason(e.target.value)}
              placeholder="Add a note for the audit trail…"
              rows={3}
            />
          </div>

          {markActionError && (
            <Alert variant="destructive">
              <AlertDescription>{markActionError}</AlertDescription>
            </Alert>
          )}

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={closeMarkActionDialog}
              disabled={markActionLoading}
            >
              Cancel
            </Button>
            <Button
              type="button"
              size="sm"
              onClick={confirmMarkAction}
              disabled={
                markActionLoading ||
                !markActionTarget ||
                !resolveRowTransactionId(markActionTarget.row)
              }
            >
              {markActionLoading
                ? 'Saving…'
                : `Mark as ${markActionTarget?.kind === 'CHARGEBACK' ? 'Chargeback' : 'Refund'}`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TransactionMonitoring;
